import { TransportItemType, type TransportItem } from '@grafana/faro-core';
import { routes } from '../routes';

const routeTemplates = new Set(Object.values(routes).map((route) => route.pattern));
const lifecycleEvents = new Set(['session_start', 'session_resume', 'session_extend']);
const vitalNames = new Set(['cls', 'fcp', 'fid', 'inp', 'lcp', 'ttfb']);
const errorTypes = new Set([
  'Error',
  'TypeError',
  'RangeError',
  'ReferenceError',
  'SyntaxError',
  'URIError',
  'EvalError',
  'UnhandledRejection',
]);
const traceAttributes = new Set([
  'session.id',
  'http.method',
  'http.request.method',
  'http.status_code',
  'http.response.status_code',
]);
const resourceAttributes = new Set([
  'service.name',
  'service.version',
  'deployment.environment',
  'deployment.environment.name',
]);

export function sanitizeFaroItem(item: TransportItem): TransportItem | null {
  const meta = {
    app: { name: 'AutoScript Frontend', version: item.meta.app?.version, environment: item.meta.app?.environment },
    session: {
      id: item.meta.session?.id,
      ...(item.meta.session?.attributes?.isSampled === 'true' || item.meta.session?.attributes?.isSampled === 'false'
        ? { attributes: { isSampled: item.meta.session.attributes.isSampled } }
        : {}),
    },
  };
  const payload = item.payload;
  const trace =
    'trace' in payload &&
    payload.trace &&
    /^[a-f0-9]{32}$/i.test(payload.trace.trace_id) &&
    /^[a-f0-9]{16}$/i.test(payload.trace.span_id)
      ? { ['trace_id']: payload.trace.trace_id, ['span_id']: payload.trace.span_id }
      : undefined;
  if (item.type === TransportItemType.EVENT && 'name' in payload) {
    if (payload.name === 'route_change') {
      const toRoute = payload.attributes?.toRoute;
      if (!toRoute || !routeTemplates.has(toRoute)) {
        return null;
      }
      const fromRoute = payload.attributes?.fromRoute;
      return {
        type: item.type,
        meta,
        payload: {
          name: 'route_change',
          timestamp: payload.timestamp,
          attributes: { toRoute, ...(fromRoute && routeTemplates.has(fromRoute) ? { fromRoute } : {}) },
        },
      };
    }
    if (!lifecycleEvents.has(payload.name)) {
      return null;
    }
    return { type: item.type, meta, payload: { name: payload.name, timestamp: payload.timestamp } };
  }
  if (item.type === TransportItemType.EXCEPTION && 'value' in payload) {
    return {
      type: item.type,
      meta,
      payload: {
        trace,
        type: errorTypes.has(payload.type) ? payload.type : 'Error',
        value: 'Browser error (message redacted)',
        timestamp: payload.timestamp,
        stacktrace: {
          frames: (payload.stacktrace?.frames ?? []).map((frame) => {
            let filename = 'redacted';
            try {
              const url = new URL(frame.filename, window.location.origin);
              if (
                url.origin === window.location.origin &&
                /^\/(?:src|assets)\/[a-zA-Z0-9_./-]+\.(?:[jt]sx?)$/.test(url.pathname)
              ) {
                filename = url.pathname;
              }
            } catch {
              /* Unparseable source names stay redacted. */
            }
            return {
              filename,
              function: /^[a-zA-Z_$][\w.$<>]*$/.test(frame.function) ? frame.function : 'redacted',
              lineno: frame.lineno,
              colno: frame.colno,
            };
          }),
        },
      },
    };
  }
  if (item.type === TransportItemType.MEASUREMENT && 'values' in payload) {
    if (payload.type !== 'web-vitals') {
      return null;
    }
    const values = Object.fromEntries(
      Object.entries(payload.values).filter(
        ([key, value]) => vitalNames.has(key.toLowerCase()) && Number.isFinite(value),
      ),
    );
    if (!Object.keys(values).length) {
      return null;
    }
    return { type: item.type, meta, payload: { type: 'web-vitals', values, timestamp: payload.timestamp } };
  }
  if (item.type === TransportItemType.TRACE && 'resourceSpans' in payload) {
    return {
      type: item.type,
      meta,
      payload: {
        resourceSpans: payload.resourceSpans?.map((resource) => ({
          resource: {
            attributes:
              resource.resource?.attributes?.filter((attribute) => resourceAttributes.has(attribute.key)) ?? [],
            droppedAttributesCount: 0,
          },
          scopeSpans: resource.scopeSpans?.map((scope) => ({
            scope: scope.scope ? { name: scope.scope.name, version: scope.scope.version } : undefined,
            spans: scope.spans
              ?.filter((span) => span.kind === 3)
              .map((span) => ({
                traceId: span.traceId,
                spanId: span.spanId,
                parentSpanId: span.parentSpanId,
                kind: span.kind,
                startTimeUnixNano: span.startTimeUnixNano,
                endTimeUnixNano: span.endTimeUnixNano,
                name: /^(?:HTTP )?(GET|POST|PUT|PATCH|DELETE|HEAD|OPTIONS)$/.exec(span.name)?.[1] ?? 'HTTP',
                status: { code: span.status.code },
                flags: span.flags,
                droppedAttributesCount: 0,
                events: [],
                droppedEventsCount: 0,
                links: [],
                droppedLinksCount: 0,
                attributes: [
                  ...(span.attributes?.filter((attribute) => traceAttributes.has(attribute.key)) ?? []),
                  { key: 'url.full', value: { stringValue: `${window.location.origin}/api` } },
                ],
              })),
          })),
        })),
      },
    };
  }
  return null;
}
